// skipcq: JS-W1028
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { BrewerySessionContext } from '../../context/BrewerySessionContext';
import useBrewhouseBeerCatalog from '../../hooks/useBrewhouseBeerCatalog';
import useBeerReviewTags from '../../hooks/useBeerReviewTags';
import useBeerStyles from '../../hooks/useBeerStyles';
import {
  trackBrewhouseBeerReviewStatsSectionEmpty,
  trackBrewhouseBeerReviewStatsView,
  trackBrewhouseDashboardClickBeerRow,
  trackBrewhouseDashboardClickManageBeer,
} from '../../utils/brewhousePortalAnalytics';
import { buildBeerStyleLabelMap } from '../../utils/brewhouseBeerStats';
import { PRIMARY_DETAIL_TEXT } from '../../utils/theme';
import PrimaryActionButton from '../common/PrimaryActionButton';
import BrewhouseDashboardCard from './BrewhouseDashboardCard';
import BeerReviewStatsSection from './BeerReviewStatsSection';

/**
 * Brewhouse dashboard card — top-rated beers, then recently added, from brewery catalog.
 */
function BeerReviewStatsCard() {
  const session = useContext(BrewerySessionContext);
  const breweryId = session.breweryId;
  const beerScopeBreweryIds =
    session.activeBeerScopeBreweryIds?.length > 0
      ? session.activeBeerScopeBreweryIds
      : breweryId
        ? [breweryId]
        : [];
  const { recentlyAdded, topRated, loading, error } = useBrewhouseBeerCatalog(beerScopeBreweryIds);
  const { tagLabelMap, loading: tagsLoading } = useBeerReviewTags();
  const { beerStyles, loading: stylesLoading } = useBeerStyles();
  const styleLabelMap = useMemo(() => buildBeerStyleLabelMap(beerStyles), [beerStyles]);
  const trackedViewRef = useRef(false);
  const trackedEmptySectionsRef = useRef(new Set());

  const isLoading = loading || tagsLoading || stylesLoading;

  useEffect(() => {
    if (!breweryId || isLoading || trackedViewRef.current) {
      return;
    }

    trackBrewhouseBeerReviewStatsView({
      breweryId,
      recentlyAddedCount: recentlyAdded.length,
      topRatedCount: topRated.length,
    });
    trackedViewRef.current = true;
  }, [breweryId, isLoading, recentlyAdded.length, topRated.length]);

  useEffect(() => {
    if (!breweryId || isLoading) {
      return;
    }

    if (recentlyAdded.length === 0 && !trackedEmptySectionsRef.current.has('recently-added')) {
      trackBrewhouseBeerReviewStatsSectionEmpty({
        breweryId,
        section: 'recently-added',
      });
      trackedEmptySectionsRef.current.add('recently-added');
    }

    if (topRated.length === 0 && !trackedEmptySectionsRef.current.has('top-rated')) {
      trackBrewhouseBeerReviewStatsSectionEmpty({
        breweryId,
        section: 'top-rated',
      });
      trackedEmptySectionsRef.current.add('top-rated');
    }
  }, [breweryId, isLoading, recentlyAdded.length, topRated.length]);

  const handleManageBeerClick = useCallback(() => {
    if (!breweryId) {
      return;
    }
    try {
      trackBrewhouseDashboardClickManageBeer({ breweryId });
    } catch {
      // Navigation must succeed even when analytics capture fails.
    }
  }, [breweryId]);

  const handleBeerRowClick = useCallback(
    ({ breweryId: rowBreweryId, beerId, section }) => {
      try {
        trackBrewhouseDashboardClickBeerRow({
          breweryId: rowBreweryId,
          beerId,
          section,
        });
      } catch {
        // Navigation must succeed even when analytics capture fails.
      }
    },
    [],
  );

  if (isLoading) {
    return (
      <BrewhouseDashboardCard>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="primary" size={32} />
        </CardContent>
      </BrewhouseDashboardCard>
    );
  }

  if (error) {
    return (
      <BrewhouseDashboardCard>
        <CardContent>
          <Typography color="error">{error}</Typography>
        </CardContent>
      </BrewhouseDashboardCard>
    );
  }

  return (
    <BrewhouseDashboardCard>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{ color: PRIMARY_DETAIL_TEXT, mb: 2 }}
        >
          Beer Review Stats
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <BeerReviewStatsSection
            title="Top Rated"
            section="top-rated"
            beers={topRated}
            tagLabelMap={tagLabelMap}
            styleLabelMap={styleLabelMap}
            breweryId={breweryId}
            onRowClick={handleBeerRowClick}
          />
          <BeerReviewStatsSection
            title="Recently Added"
            section="recently-added"
            beers={recentlyAdded}
            tagLabelMap={tagLabelMap}
            styleLabelMap={styleLabelMap}
            breweryId={breweryId}
            onRowClick={handleBeerRowClick}
          />
        </Box>
        <PrimaryActionButton
          component={RouterLink}
          to="/brewery-portal/pint-portfolio"
          onClick={handleManageBeerClick}
          sx={{ mt: 3 }}
        >
          Manage beer
        </PrimaryActionButton>
      </CardContent>
    </BrewhouseDashboardCard>
  );
}

export default BeerReviewStatsCard;
